import type {
  AdminFoodRequestSource,
  FoodRequestKindName,
  FoodResearchFieldName,
} from '@steado/admin-contract/foods';
import { RotateCcwIcon } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { TONE_DOTS } from '@/components/table/tone';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import {
  CONFIDENCE_LABELS,
  CONFIDENCE_TONES,
  FOOD_STATE_LABELS,
  KIND_LABELS,
  KIND_TONES,
  RESEARCH_FIELD_LABELS,
  type ConfidenceLevel,
} from './request-copy';

/** The pieces every review screen shares: chips, keys, sources, and the reject and research forms. */

export const ADMIN_ONLY = 'Answering a food request requires the admin role.';

export function KindBadge({ kind }: { readonly kind: FoodRequestKindName }) {
  return (
    <Badge size="sm" tone={KIND_TONES[kind]}>
      {KIND_LABELS[kind]}
    </Badge>
  );
}

/** "• Medium": a dot in the confidence's tone, and its word. */
export function ConfidenceDot({
  level,
  suffix,
  className,
}: {
  readonly level: ConfidenceLevel;
  readonly suffix?: string;
  readonly className?: string;
}) {
  return (
    <span className={cn('inline-flex items-center gap-[7px] font-medium', className)}>
      <span
        aria-hidden="true"
        className={cn('size-[7px] shrink-0 rounded-full', TONE_DOTS[CONFIDENCE_TONES[level]])}
      />
      {CONFIDENCE_LABELS[level]}
      {suffix}
    </span>
  );
}

/** "Medium confidence" as a chip, beside a request's title. */
export function ConfidenceBadge({ level }: { readonly level: ConfidenceLevel }) {
  return (
    <Badge size="sm" tone={CONFIDENCE_TONES[level]}>
      <span aria-hidden="true" className={cn('size-1.5 rounded-full', TONE_DOTS[CONFIDENCE_TONES[level]])} />
      {level === 'none' ? CONFIDENCE_LABELS.none : `${CONFIDENCE_LABELS[level]} confidence`}
    </Badge>
  );
}

export function Kbd({ children }: { readonly children: ReactNode }) {
  return (
    <kbd className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[5px] border border-input bg-card px-1 font-mono text-xs text-muted-foreground">
      {children}
    </kbd>
  );
}

/** "K keep current · U use new · Enter apply", read out as one sentence. */
export function KeyHints({
  label,
  keys,
}: {
  readonly label: string;
  readonly keys: readonly [string, string][];
}) {
  return (
    <span
      aria-label={label}
      className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground"
    >
      {keys.map(([key, what], index) => (
        <span key={key} aria-hidden="true" className="inline-flex items-center gap-1">
          <Kbd>{key}</Kbd>
          {what === '' ? null : (
            <span>
              {what}
              {index < keys.length - 1 ? ' ·' : ''}
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

/** "[1] [2]": a field's sources, each a jump to its entry on the page. */
export function SourceRefs({
  indexes,
  idPrefix,
}: {
  readonly indexes: readonly number[];
  readonly idPrefix: string;
}) {
  if (indexes.length === 0) return <span className="text-xs text-muted-foreground">None cited</span>;
  return (
    <span className="inline-flex flex-wrap gap-x-1 align-baseline">
      {indexes.map((index) => (
        <a
          key={index}
          href={`#${idPrefix}-${String(index + 1)}`}
          aria-label={`Source ${String(index + 1)}`}
          className="inline-flex min-h-6 min-w-6 items-center font-mono text-xs text-info-fg no-underline hover:underline"
        >
          [{index + 1}]
        </a>
      ))}
    </span>
  );
}

function supportsText(source: AdminFoodRequestSource): string {
  return source.supports.length === 0
    ? 'not cited for a field'
    : source.supports.map((field: FoodResearchFieldName) => RESEARCH_FIELD_LABELS[field]).join(', ');
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** One source: its title linking out, raw or cooked, an excerpt, and what it supports. */
export function SourceItem({
  source,
  index,
  idPrefix,
  compact = false,
}: {
  readonly source: AdminFoodRequestSource;
  readonly index: number;
  readonly idPrefix?: string;
  readonly compact?: boolean;
}) {
  const title = source.title ?? (source.url === null ? 'Steado’s catalog' : hostOf(source.url));
  return (
    <li
      id={idPrefix === undefined ? undefined : `${idPrefix}-${String(index + 1)}`}
      className="grid scroll-mt-20 grid-cols-[28px_minmax(0,1fr)] gap-1 border-t border-border px-[18px] py-2.5"
    >
      <span className="pt-px font-mono text-xs text-info-fg">[{index + 1}]</span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          {source.url === null ? (
            <span className="font-medium text-foreground">{title}</span>
          ) : (
            <a
              href={source.url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex min-h-6 items-center font-medium break-words text-foreground underline-offset-2 hover:underline"
            >
              {title}
            </a>
          )}
          <span className="text-xs text-muted-foreground">{FOOD_STATE_LABELS[source.foodState]}</span>
        </span>
        {compact || source.excerpt.trim() === '' ? null : (
          <span className="max-w-[70ch] text-[13px] leading-normal break-words text-foreground">
            “{source.excerpt.trim()}”
          </span>
        )}
        <span className="flex flex-wrap gap-x-2.5 text-xs text-muted-foreground">
          <span>Supports: {supportsText(source)}</span>
          {source.url === null ? null : <span className="ml-auto">{hostOf(source.url)}</span>}
        </span>
      </span>
    </li>
  );
}

export function SourcesPanel({
  title,
  sources,
  idPrefix,
  className,
}: {
  readonly title: string;
  readonly sources: readonly AdminFoodRequestSource[];
  readonly idPrefix: string;
  readonly className?: string;
}) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      className={cn('overflow-hidden rounded-xl border border-border bg-card', className)}
    >
      <header className="flex items-center px-[18px] pt-3.5 pb-3">
        <h2 id={id} className="text-[15px] font-semibold text-foreground">
          {title}
        </h2>
        <span className="ml-auto font-mono text-xs text-muted-foreground">{sources.length}</span>
      </header>
      {sources.length === 0 ? (
        <p className="border-t border-border px-[18px] py-3 text-[13px] text-muted-foreground">
          The research kept no sources for this request.
        </p>
      ) : (
        <ol>
          {sources.map((source, index) => (
            <SourceItem
              key={`${source.url ?? 'catalog'}-${String(index)}`}
              source={source}
              index={index}
              idPrefix={idPrefix}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

const REJECT_REASONS = ['Not a food', 'Too vague to be one dish'] as const;

/**
 * Rejecting needs a reason: the next reviewer reads it. Two common ones a
 * click away, anything else typed; the button waits, and says why, until
 * there is one.
 */
export function RejectForm({
  canMutate,
  isPending,
  error,
  onReject,
  onCancel,
  compact = false,
}: {
  readonly canMutate: boolean;
  readonly isPending: boolean;
  readonly error: string | null;
  readonly onReject: (reason: string) => void;
  readonly onCancel?: () => void;
  readonly compact?: boolean;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const [other, setOther] = useState('');
  const name = useId();
  const otherId = useId();
  const whyId = useId();
  const reason = picked === 'other' ? other.trim() : (picked ?? '');
  const why = !canMutate
    ? ADMIN_ONLY
    : picked === null
      ? 'Pick a reason first.'
      : reason.length < 3
        ? 'Say why in a few words first.'
        : null;

  return (
    <div className="flex flex-col gap-2">
      <fieldset className="flex flex-col">
        <legend className={cn('pb-1 text-xs text-muted-foreground', compact && 'sr-only')}>
          Why, so the next reviewer knows
        </legend>
        {[...REJECT_REASONS, 'other'].map((option) => (
          <label
            key={option}
            className="flex min-h-8 cursor-pointer items-center gap-2 text-[13px] text-foreground"
          >
            <input
              type="radio"
              name={name}
              checked={picked === option}
              onChange={() => {
                setPicked(option);
              }}
              className="size-4 accent-primary"
            />
            {option === 'other' ? 'Something else' : option}
          </label>
        ))}
      </fieldset>
      {picked === 'other' ? (
        <div className="flex flex-col gap-1">
          <label htmlFor={otherId} className="text-xs font-medium text-foreground">
            Why <span className="font-normal text-muted-foreground">(required)</span>
          </label>
          <Textarea
            id={otherId}
            rows={2}
            maxLength={500}
            value={other}
            onChange={(event) => {
              setOther(event.target.value);
            }}
            placeholder="Already in the catalog as Ragi mudde"
            className="text-[13px]"
          />
        </div>
      ) : null}
      {error === null ? null : (
        <p role="alert" className="text-[12.5px] font-medium text-err-fg">
          {error}
        </p>
      )}
      <span className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          aria-disabled={why !== null || isPending}
          aria-describedby={why === null ? undefined : whyId}
          className={cn(
            'border-err-fg/30 text-err-fg hover:bg-err-bg hover:text-err-fg',
            (why !== null || isPending) && 'cursor-not-allowed opacity-60 hover:bg-card',
          )}
          onClick={() => {
            if (why === null && !isPending) onReject(reason);
          }}
        >
          {isPending ? 'Rejecting…' : 'Reject'}
        </Button>
        {onCancel === undefined ? null : (
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
        {why === null ? null : (
          <span id={whyId} className="text-xs text-muted-foreground">
            {why}
          </span>
        )}
      </span>
    </div>
  );
}

/** Research again, steered by a note; it comes back to the queue when its sources are in. */
export function ResearchForm({
  canMutate,
  isPending,
  error,
  placeholder,
  onResearch,
}: {
  readonly canMutate: boolean;
  readonly isPending: boolean;
  readonly error: string | null;
  readonly placeholder: string;
  readonly onResearch: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  const id = useId();
  const whyId = useId();
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="max-w-[70ch] text-xs text-muted-foreground">
        Tell the AI what to look for. It reruns with your note and comes back to the queue.
      </label>
      <Textarea
        id={id}
        rows={3}
        maxLength={200}
        value={note}
        onChange={(event) => {
          setNote(event.target.value);
        }}
        placeholder={placeholder}
        className="text-[13px]"
      />
      {error === null ? null : (
        <p role="alert" className="text-[12.5px] font-medium text-err-fg">
          {error}
        </p>
      )}
      <span className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          aria-disabled={!canMutate || isPending}
          aria-describedby={canMutate ? undefined : whyId}
          className={cn((!canMutate || isPending) && 'cursor-not-allowed opacity-60')}
          onClick={() => {
            if (canMutate && !isPending) onResearch(note);
          }}
        >
          <RotateCcwIcon aria-hidden="true" className="size-3.5" />
          {isPending ? 'Queuing…' : 'Research again'}
        </Button>
        {canMutate ? null : (
          <span id={whyId} className="text-xs text-muted-foreground">
            {ADMIN_ONLY}
          </span>
        )}
      </span>
    </div>
  );
}

/** A section card with the design's 15px heading, for the review pages. */
export function Card({
  title,
  eyebrow,
  description,
  action,
  children,
  className,
  id,
  tone,
}: {
  readonly title: ReactNode;
  /** A line above the title: "Evidence for". */
  readonly eyebrow?: string;
  readonly description?: ReactNode;
  readonly action?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  readonly id?: string;
  readonly tone?: 'warn';
}) {
  const headingId = useId();
  return (
    <section
      id={id}
      aria-labelledby={headingId}
      className={cn(
        'min-w-0 scroll-mt-20 overflow-hidden rounded-xl border bg-card',
        tone === 'warn' ? 'border-warn-fg/25' : 'border-border',
        className,
      )}
    >
      <header
        className={cn(
          'flex flex-wrap items-center gap-x-2.5 gap-y-1 px-[18px] pt-3.5 pb-3',
          tone === 'warn' && 'bg-warn-bg py-3',
        )}
      >
        {eyebrow === undefined ? (
          <h2 id={headingId} className="text-[15px] font-semibold text-foreground">
            {title}
          </h2>
        ) : (
          <span className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">{eyebrow}</span>
            <h2 id={headingId} className="text-[15px] font-semibold text-foreground">
              {title}
            </h2>
          </span>
        )}
        {description === undefined ? null : (
          <span className="text-xs text-pretty text-muted-foreground">{description}</span>
        )}
        {action === undefined ? null : <span className="ml-auto">{action}</span>}
      </header>
      {children}
    </section>
  );
}
